import {
  Box,
  Grid,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import AlertMessages from "common/Alert/AlertMessages";
import { useSnackbar } from "notistack";
import SimpleModal from "common/Form/Form";
import { useNavigate, useParams } from "react-router-dom";
import ComponentHeaderTwo from "common/ComponentHeaderTwo/ComponentHeaderTwo";
import { ReactComponent as Packages } from "assets/SideBarIcons/pakage.svg";
import { checkPrivileges } from "Helpers/CheckPrivileges";
import { useBusinessSetupEntityBreadcrumbs } from "Helpers/useBusinessSetupEntityBreadcrumbs";
import { useEffect, useRef, useState } from "react";
import { GetPackageByIdResponse } from "subModule/src/services/packages/interface";
import Loader from "layout/Loader";
import DynamicSelect from "common/Select/Select";
import {
  PackagesProfileService,
  TaxProcedureProfileService,
} from "subModule/src/core/services";
import { useSelector } from "react-redux";
import { selectUserInfo } from "features/user/userSlice";
import CommonCheckbox from "common/CheckBox/Checkbox";
import PackageTabs from "./PackageTabs";
import { useGetPackageGroupDropdownService } from "subModule/src/services/packagegroup/usePackageGroup";
import FormDialog from "common/Modal/Modal";
import { ReactComponent as ModalBtn } from "assets/Modal/ModalBtn.svg";
import { useGetTaxProcedureDropdownService } from "subModule/src/services/taxProcedure/useTaxProcedure";
import Currency from "common/Others/Currency";
import { GetDefaultTaxProcedureResponse } from "subModule/src/services/taxProcedure/interface";
import { useGetItemGroupDropdownService } from "subModule/src/services/itemgroup/useItemGroup";
import PackageGroupModal from "pages/PackageGroup/PackageGroupModal";
import { themes } from "theme/themes";
import { brandingInfo } from "features/server/brandingSlice";
import { Info } from "@mui/icons-material";
interface FormData {
  [key: string]: any;
}
export default function PackagesModal() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams();
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);
  const { enqueueSnackbar } = useSnackbar();
  const branding = useSelector(brandingInfo);
  const themeColors =
    branding?.attributes?.themeId === 100
      ? branding?.attributes?.customTheme
      : (themes as any)[branding?.attributes?.themeId || 3];
  const {
    register,
    control,
    handleSubmit,
    formState: { errors, dirtyFields, isDirty },
    reset,
    setValue,
    watch,
  } = useForm<FormData>({
    defaultValues: {
      applyToAllStations: true,
      pricingType: { value: 2, label: t("serviceSum") },
      itemLimit: null,
    },
  });
  const [loading, setLoading] = useState<boolean>(params?.id ? true : false);
  const [IsBtnEnabled, setIsBtnEnabled] = useState<boolean>(
    params?.id ? false : true,
  );
  const { breadcrumbItems, captureLabel } = useBusinessSetupEntityBreadcrumbs({
    sectionLabelKey: "Items",
    moduleLabelKey: "packages",
    moduleHref: "/packages",
    entityFallbackLabelKey: "title",
    isEdit: !!params?.id,
  });
  const [addNew, setAddNew] = useState<boolean>(false);
  const [submittedFormData, setSubmittedFormData] = useState<FormData | null>(
    null,
  );
  const [dropdownSearchTerm, setDropdownSearchTerm] = useState<any>("");
  const [pageNo, setPageNo] = useState<number>(1);
  const [searchLoading, setSearchLoading] = useState<string | null>(null);
  const loggedInUserData = useSelector(selectUserInfo);
  const primaryLanguage = loggedInUserData?.business?.primaryLanguage;
  const secondaryLanguage = loggedInUserData?.business?.secondaryLanguage;
  const isArabicLanguage = (lang?: string) => {
    const l = (lang || "").toLowerCase();
    return l === "arabic" || l === "ar" || l.includes("arab");
  };
  const localizedPackageTitlePlaceholder = (lang?: string) =>
    isArabicLanguage(lang) ? t("phPackageTitleAr") : t("phPackageTitleEn");
  const loggedInUserCurrency = loggedInUserData?.business?.currency;
  const defaultPriceList = loggedInUserData?.business?.priceListTitle;
  const { data: itemGroupsData, refetch: itemGroupsRefetch } =
    useGetItemGroupDropdownService({
      pageNumber: pageNo,
      size: 50,
      title: dropdownSearchTerm,
    });
  const { data: taxProceduresData, refetch: taxProceduresRefetch } =
    useGetTaxProcedureDropdownService({
      pageNumber: pageNo,
      size: 50,
      title: dropdownSearchTerm,
    });
  const { data: packageGroupsData, refetch: packageGroupsRefetch } =
    useGetPackageGroupDropdownService({
      pageNumber: pageNo,
      size: 50,
      title: dropdownSearchTerm,
    });
  const itemGroups = (itemGroupsData?.pages[0]?.data?.content || []).map(
    (item: { id?: number; title?: string }) => ({
      value: item.id,
      label: item.title,
    }),
  );
  const taxProcedures = (taxProceduresData?.pages[0]?.data?.content || []).map(
    (item: { id: number; title: string; percentageValue?: number }) => ({
      value: item.id,
      label: item.title,
      percentageValue: item.percentageValue,
    }),
  );
  const packageGroups = (packageGroupsData?.pages[0]?.data?.content || []).map(
    (item: { id?: number; title?: string }) => ({
      value: item.id,
      label: item.title,
    }),
  );
  const groupOptions = [
    { value: 1, label: t("groups") },
    { value: 2, label: t("open") },
    { value: 3, label: t("selected") },
    { value: 4, label: t("bundled") },
  ];
  const pricingTypes = [
    { value: 1, label: t("fixed") },
    { value: 2, label: t("serviceSum") },
  ];
  const currencyOptions = Currency.map((item) => ({
    value: item.value,
    label: item.title,
    symbol: item.symbol,
  }));
  const currencyLabel =
    currencyOptions.find((option: any) => option.value === loggedInUserCurrency)
      ?.symbol || "";
  useEffect(() => {
    itemGroupsRefetch();
    taxProceduresRefetch();
    packageGroupsRefetch();
    if (!params?.id) getDefaultTaxProcedure();
  }, []);
  useEffect(() => {
    if (
      params?.id &&
      itemGroupsData &&
      packageGroupsData &&
      taxProceduresData
    ) {
      if (loading) getByID();
    }
  }, [params, itemGroupsData, packageGroupsData, taxProceduresData]);
  useEffect(() => {
    if (isDirty && params?.id) setIsBtnEnabled(true);
  }, [isDirty, dirtyFields]);
  const getDefaultTaxProcedure = () => {
    TaxProcedureProfileService.getActiveDefaultTaxProcedure(4)
      .then((res: GetDefaultTaxProcedureResponse) => {
        if (res.success) {
          setValue("taxProcedureId", {
            value: res.data?.id,
            label: res.data?.title,
            percentageValue: res.data?.percentageValue,
          });
          setLoading(false);
        }
      })
      .catch((err: any) => {
        console.error("===", err);
      });
  };
  const getByID = () => {
    PackagesProfileService.getPackageById(+(params as any)?.id)
      .then((res: GetPackageByIdResponse) => {
        if (res.success) {
          reset({
            ...(res?.data || {}),
            itemLimit: res?.data?.itemLimit || null,
            itemGroup: res.data?.itemGroup
              ? {
                  value: res.data?.itemGroup,
                  label:
                    groupOptions.find((v) => v.value === res.data?.itemGroup)
                      ?.label || res.data?.itemGroup,
                }
              : null,
            itemGroupId: res.data?.itemGroupId
              ? {
                  value: res.data?.itemGroupId,
                  label: res?.data?.itemGroupTitle || res.data?.itemGroupId,
                }
              : null,
            taxProcedureId: res.data?.taxProcedureId
              ? {
                  value: res.data?.taxProcedureId,
                  label:
                    res?.data?.taxProcedureTitle || res.data?.taxProcedureId,
                  percentageValue: res?.data?.taxPercentageValue || 0,
                }
              : null,
            pricingType: res.data?.pricingType
              ? {
                  value: res.data?.pricingType,
                  label: pricingTypes.find(
                    (v) => v.value === res.data?.pricingType,
                  )?.label,
                }
              : null,
            packageGroupId: res.data?.packageGroupId
              ? {
                  value: res.data?.packageGroupId,
                  label:
                    res.data?.packageGroupTitle || res.data?.packageGroupId,
                }
              : null,
            taxValue: (
              (res?.data.price || 0) *
              ((res?.data?.taxPercentageValue || 0) / 100)
            )?.toFixed(2),
          });
          setSubmittedFormData({
            ...(res?.data || {}),
            itemLimit: res?.data?.itemLimit || null,
            itemGroup: res.data?.itemGroup
              ? {
                  value: res.data?.itemGroup,
                  label:
                    groupOptions.find((v) => v.value === res.data?.itemGroup)
                      ?.label || res.data?.itemGroup,
                }
              : null,
            itemGroupId: res.data?.itemGroupId
              ? {
                  value: res.data?.itemGroupId,
                  label: res?.data?.itemGroupTitle || res.data?.itemGroupId,
                }
              : null,
            taxProcedureId: res.data?.taxProcedureId
              ? {
                  value: res.data?.taxProcedureId,
                  label:
                    res?.data?.taxProcedureTitle || res.data?.taxProcedureId,
                  percentageValue: res?.data?.taxPercentageValue || 0,
                }
              : null,
            pricingType: res.data?.pricingType
              ? {
                  value: res.data?.pricingType,
                  label: pricingTypes.find(
                    (v) => v.value === res.data?.pricingType,
                  )?.label,
                }
              : null,
            packageGroupId: res.data?.packageGroupId
              ? {
                  value: res.data?.packageGroupId,
                  label:
                    res?.data?.packageGroupTitle || res.data?.packageGroupId,
                }
              : null,
            taxValue: (
              (res?.data.price || 0) *
              ((res?.data?.taxPercentageValue || 0) / 100)
            )?.toFixed(2),
          });
          captureLabel(res?.data?.title);
          setLoading(false);
          setValue(
            "priceWithTax",
            parseFloat(watch("price")) + parseFloat(watch("taxValue")),
          );
        } else {
          enqueueSnackbar(
            <AlertMessages text={res.message || "Something went wrong"} />,
            { variant: "error" },
          );
        }
      })
      .catch((err: any) => {
        console.error("===", err);
      });
  };
  const onSubmit = (data: any) => {
    setIsBtnEnabled(false);
    delete data.taxValue;
    delete data.priceWithTax;
    delete data.tax;
    if (params?.id) {
      PackagesProfileService.updatePackages(+params?.id, {
        ...data,
        itemGroupId: data?.itemGroupId?.value || 0,
        taxProcedureId:
          data?.isTaxable && data?.taxProcedureId?.value
            ? data?.taxProcedureId?.value
            : 0,
        pricingType: data?.pricingType?.value || 0,
        itemGroup: data?.itemGroup?.value || 0,
        packageGroupId: data?.packageGroupId?.value || 0,
        itemLimit: data?.itemLimit ? +data?.itemLimit : null,
      })
        .then((response: any) => {
          if (response.success) {
            captureLabel(data?.title);
            setSubmittedFormData(data);
            getByID();
            enqueueSnackbar(
              <AlertMessages
                statusCode={response.status}
                text={
                  t("package") + " " + t("updated") + " " + t("successfully")
                }
              />,
              { variant: "success" },
            );
          } else {
            enqueueSnackbar(<AlertMessages text={t(response?.message)} />, {
              variant: "error",
            });
            setIsBtnEnabled(true);
          }
        })
        .catch((err: any) => {
          console.error("===", err);
          setIsBtnEnabled(true);
          enqueueSnackbar(
            <AlertMessages
              text={
                err?.message || err?.data?.message || "Something went wrong"
              }
            />,
            { variant: "error" },
          );
        });
    } else {
      PackagesProfileService.createPackages({
        ...data,
        itemGroupId: data?.itemGroupId?.value || 0,
        taxProcedureId:
          data?.isTaxable && data?.taxProcedureId?.value
            ? data?.taxProcedureId?.value
            : 0,
        pricingType: data?.pricingType?.value || 0,
        itemGroup: data?.itemGroup?.value || 0,
        packageGroupId: data?.packageGroupId?.value || 0,
        itemLimit: data?.itemLimit ? +data?.itemLimit : null,
      })
        .then((response: any) => {
          if (response.success) {
            navigate("/packages");
            enqueueSnackbar(
              <AlertMessages
                statusCode={response.status}
                text={
                  t("package") + " " + t("created") + " " + t("successfully")
                }
              />,
              { variant: "success" },
            );
          } else {
            setIsBtnEnabled(true);
            enqueueSnackbar(<AlertMessages text={t(response?.message)} />, {
              variant: "error",
            });
          }
        })
        .catch((err: any) => {
          console.error("===", err);
          setIsBtnEnabled(true);
          enqueueSnackbar(
            <AlertMessages
              text={
                err?.message || err?.data?.message || "Something went wrong"
              }
            />,
            { variant: "error" },
          );
        });
    }
  };
  const handleDropdownActions = (
    typeName: string,
    action: "searchIt" | "loadNext",
    search?: string,
  ) => {
    if (action === "searchIt") {
      setPageNo(1);
      setDropdownSearchTerm(search);
      if (search) setSearchLoading(typeName);
    } else {
      setPageNo(pageNo + 1);
      setSearchLoading(typeName);
    }
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      if (typeName === "itemGroups")
        itemGroupsRefetch().finally(() => setSearchLoading(null));
      else if (typeName === "packageGroups")
        packageGroupsRefetch().finally(() => setSearchLoading(null));
      else if (typeName === "taxProcedures")
        taxProceduresRefetch().finally(() => setSearchLoading(null));
    }, 300);
  };
  return (
    <>
      <ComponentHeaderTwo
        showBtn={true}
        showSaveBtn={true}
        onClick={() => navigate("/packages")}
        onSubmit={handleSubmit(onSubmit)}
        heading={params?.id ? t("packages") : t("addPackages")}
        icon={<Packages height={28} width={28} />}
        btnText={"back"}
        btnType={"back"}
        isBtnEnabled={IsBtnEnabled}
        showCreateBtn={checkPrivileges("ROLE_PACKAGE_CREATE")}
        breadcrumbItems={breadcrumbItems}
      />
      <div className={`${params?.id ? "flex" : ""}`}>
        {params?.id ? (
          <PackageTabs
            params={params}
            selectedIndex={0}
            formData={submittedFormData}
          />
        ) : null}
        {loading ? (
          <Loader />
        ) : (
          <SimpleModal
            visable={true}
            title={params?.id ? t("packages") : t("addPackages")}
            isButtonVisable={true}
            isVisableBtn={IsBtnEnabled}
            btnclosetext="Back"
            buttonText={params?.id ? "update" : "submit"}
            attBtnNotshow={false}
            notVisableBackbtn={true}
            showPortList={handleSubmit(onSubmit)}
            formSubmit={handleSubmit(onSubmit)}
            content={
              <>
                <Grid
                  container
                  component="form"
                  id="form"
                  spacing={2}
                  onSubmit={handleSubmit(onSubmit)}
                >
                  <Grid container spacing={2} className="my-[0px] ml-[3px]">
                    <Grid item xs={12} md={3}>
                      <TextField
                        {...register("packageCode", {
                          validate: {
                            required: (value) =>
                              value?.trim() !== "" ||
                              t("This field is required."),
                          },
                        })}
                        label={t("packageCode") + " *"}
                        placeholder={t("phPackageCode")}
                        title={t("packageCode")}
                        fullWidth
                        error={!!errors.packageCode}
                        helperText={errors.packageCode?.message as string}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        {...register("title", {
                          validate: {
                            required: (value) =>
                              value?.trim() !== "" ||
                              t("This field is required."),
                          },
                        })}
                        label={`${!primaryLanguage ? t("primary") : ""} ${t("title")} ${primaryLanguage ? ` (${t(primaryLanguage)})` : ""} *`}
                        placeholder={localizedPackageTitlePlaceholder(
                          primaryLanguage,
                        )}
                        title={`${!primaryLanguage ? t("primary") : ""} ${t("title")} ${primaryLanguage ? ` (${t(primaryLanguage)})` : ""} *`
                          .replace(/\s+/g, " ")
                          .trim()}
                        fullWidth
                        error={!!errors.title}
                        helperText={errors.title?.message as string}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        {...register("secondaryTitle")}
                        label={`${!secondaryLanguage ? t("secondary") : ""} ${t("title")} ${secondaryLanguage ? ` (${t(secondaryLanguage)})` : ""}`}
                        placeholder={localizedPackageTitlePlaceholder(
                          secondaryLanguage,
                        )}
                        title={`${!secondaryLanguage ? t("secondary") : ""} ${t("title")} ${secondaryLanguage ? ` (${t(secondaryLanguage)})` : ""}`
                          .replace(/\s+/g, " ")
                          .trim()}
                        fullWidth
                        error={!!errors.secondaryTitle}
                        helperText={errors.secondaryTitle?.message as string}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                  </Grid>
                  <Grid container spacing={2} className="my-[0px] ml-[3px]">
                    <Grid item xs={12} md={3}>
                      <Controller
                        name="itemGroup"
                        control={control}
                        defaultValue={null}
                        rules={{ required: t("This field is required.") }}
                        render={({ field }) => (
                          <DynamicSelect
                            label={t("partType") + " *"}
                            {...field}
                            options={groupOptions}
                            isSearchable
                            isClearable
                            defaultPlaceholder="phSelectPartType"
                            onChange={(selectedOption: any) => {
                              field.onChange(selectedOption);
                              setValue("itemGroupId", null);
                            }}
                            error={errors.itemGroup?.message as string}
                            description={
                              <Box>
                                <Box sx={{ marginBottom: "6px" }}>
                                  {t("partTypeInfoIntro", {
                                    item: t("package"),
                                  })}
                                </Box>
                                <Box component="ul" sx={{ margin: 0 }}>
                                  <li>
                                    <strong>{t("open")}:</strong>{" "}
                                    {t("partTypeInfoOpen")}
                                  </li>
                                  <li>
                                    <strong>{t("selected")}:</strong>{" "}
                                    {t("partTypeInfoSelected")}
                                  </li>
                                  <li>
                                    <strong>{t("groups")}:</strong>{" "}
                                    {t("partTypeInfoGroup")}
                                  </li>
                                </Box>
                              </Box>
                            }
                          />
                        )}
                      />
                    </Grid>
                    {watch("itemGroup")?.value === 1 ? (
                      <Grid item xs={12} md={3}>
                        <Controller
                          name="itemGroupId"
                          control={control}
                          defaultValue={null}
                          rules={{ required: t("This field is required.") }}
                          render={({ field }) => (
                            <DynamicSelect
                              label={t("itemGroup") + " *"}
                              {...field}
                              options={itemGroups}
                              isSearchable
                              onChange={(selectedOption: any) =>
                                field.onChange(selectedOption)
                              }
                              error={errors.itemGroupId?.message as string}
                              isLoading={searchLoading === "itemGroups"}
                              onInputChange={(
                                search: string,
                                { action }: { action: string },
                              ) => {
                                if (
                                  action === "input-change" ||
                                  (action !== "set-value" &&
                                    action !== "menu-close" &&
                                    action !== "input-blur")
                                )
                                  handleDropdownActions(
                                    "itemGroups",
                                    "searchIt",
                                    search,
                                  );
                                else if (
                                  action === "input-blur" ||
                                  action === "menu-close"
                                ) {
                                  if (dropdownSearchTerm || pageNo > 1)
                                    handleDropdownActions(
                                      "itemGroups",
                                      "searchIt",
                                      "",
                                    );
                                  else setDropdownSearchTerm("");
                                  setPageNo(1);
                                }
                              }}
                              onMenuScrollToBottom={() => {
                                if (
                                  !(itemGroupsData?.pages[0]?.data as any).last
                                )
                                  handleDropdownActions(
                                    "itemGroups",
                                    "loadNext",
                                  );
                              }}
                              page={pageNo}
                            />
                          )}
                        />
                      </Grid>
                    ) : null}
                    <Grid
                      item
                      xs={12}
                      md={3}
                      className="grid"
                      sx={{
                        gridTemplateColumns: checkPrivileges(
                          "ROLE_PACKAGEGROUP_CREATE",
                        )
                          ? "85% 10%"
                          : "100%",
                        "& .addNew path": { fill: themeColors?.primary },
                      }}
                    >
                      <Controller
                        name="packageGroupId"
                        control={control}
                        defaultValue={null}
                        rules={{ required: false }}
                        render={({ field }) => (
                          <DynamicSelect
                            label={t("packageGroup")}
                            {...field}
                            options={packageGroups}
                            isSearchable
                            isClearable
                            defaultPlaceholder="phSelectPackageGroup"
                            onChange={(selectedOption: any) =>
                              field.onChange(selectedOption)
                            }
                            error={errors.packageGroupId?.message as string}
                            isLoading={searchLoading === "packageGroups"}
                            onInputChange={(
                              search: string,
                              { action }: { action: string },
                            ) => {
                              if (
                                action === "input-change" ||
                                (action !== "set-value" &&
                                  action !== "menu-close" &&
                                  action !== "input-blur")
                              )
                                handleDropdownActions(
                                  "packageGroups",
                                  "searchIt",
                                  search,
                                );
                              else if (
                                action === "input-blur" ||
                                action === "menu-close"
                              ) {
                                if (dropdownSearchTerm || pageNo > 1)
                                  handleDropdownActions(
                                    "packageGroups",
                                    "searchIt",
                                    "",
                                  );
                                else setDropdownSearchTerm("");
                                setPageNo(1);
                              }
                            }}
                            onMenuScrollToBottom={() => {
                              if (
                                !(packageGroupsData?.pages[0]?.data as any).last
                              )
                                handleDropdownActions(
                                  "packageGroups",
                                  "loadNext",
                                );
                            }}
                            page={pageNo}
                          />
                        )}
                      />
                      {checkPrivileges("ROLE_PACKAGEGROUP_CREATE") && (
                        <Tooltip
                          onClick={() => setAddNew(true)}
                          title={t("addPackageGroup")}
                        >
                          <ModalBtn className="mt-5 mx-2 mb-2 cursor-pointer h-[38px] addNew" />
                        </Tooltip>
                      )}
                      {addNew && (
                        <FormDialog
                          open={addNew}
                          onClose={(event, reason) =>
                            event && reason !== "backdropClick"
                              ? setAddNew(false)
                              : ""
                          }
                          title={t("addPackageGroup")}
                          createPopup={true}
                          fullWidth
                          hideActions={true}
                          content={
                            <PackageGroupModal
                              setAddNew={setAddNew}
                              refetch={packageGroupsRefetch}
                            />
                          }
                        />
                      )}
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Controller
                        name="applyToAllStations"
                        control={control}
                        defaultValue={false}
                        render={({ field }) => (
                          <CommonCheckbox
                            {...field}
                            label={t("applyToAllStations")}
                            onChange={(
                              event: React.ChangeEvent<HTMLInputElement>,
                            ) => field.onChange(event.target.checked)}
                            checked={field.value}
                            color={themeColors?.primary}
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                  <Grid container spacing={2} className="my-[0px] ml-[3px]">
                    <Grid item xs={12} md={3}>
                      <Controller
                        name="isTaxable"
                        control={control}
                        defaultValue={false}
                        render={({ field }) => (
                          <CommonCheckbox
                            {...field}
                            label={t("isTaxable")}
                            onChange={(
                              event: React.ChangeEvent<HTMLInputElement>,
                            ) => {
                              field.onChange(event.target.checked);
                              if (!event.target.checked) {
                                setValue("taxProcedureId", null);
                              }
                              if (!params?.id && event.target.checked)
                                getDefaultTaxProcedure();
                            }}
                            checked={field.value}
                            color={themeColors?.primary}
                          />
                        )}
                      />
                    </Grid>
                    {watch("isTaxable") ? (
                      <Grid item xs={12} md={3}>
                        <Controller
                          name="taxProcedureId"
                          control={control}
                          defaultValue={null}
                          rules={{ required: t("This field is required.") }}
                          render={({ field }) => (
                            <DynamicSelect
                              label={t("taxProcedure") + " *"}
                              {...field}
                              options={taxProcedures}
                              isSearchable
                              onChange={(selectedOption: any) => {
                                field.onChange(selectedOption);
                                setValue(
                                  "taxValue",
                                  (
                                    watch("price") *
                                    (watch("taxProcedureId")?.percentageValue /
                                      100)
                                  )?.toFixed(2),
                                );
                                setValue(
                                  "priceWithTax",
                                  parseFloat(watch("price")) +
                                    watch("price") *
                                      (selectedOption.percentageValue / 100),
                                );
                              }}
                              error={errors.taxProcedureId?.message as string}
                              isLoading={searchLoading === "taxProcedures"}
                              onInputChange={(
                                search: string,
                                { action }: { action: string },
                              ) => {
                                if (
                                  action === "input-change" ||
                                  (action !== "set-value" &&
                                    action !== "menu-close" &&
                                    action !== "input-blur")
                                )
                                  handleDropdownActions(
                                    "taxProcedures",
                                    "searchIt",
                                    search,
                                  );
                                else if (
                                  action === "input-blur" ||
                                  action === "menu-close"
                                ) {
                                  if (dropdownSearchTerm || pageNo > 1)
                                    handleDropdownActions(
                                      "taxProcedures",
                                      "searchIt",
                                      "",
                                    );
                                  else setDropdownSearchTerm("");
                                  setPageNo(1);
                                }
                              }}
                              onMenuScrollToBottom={() => {
                                if (
                                  !(taxProceduresData?.pages[0]?.data as any)
                                    .last
                                )
                                  handleDropdownActions(
                                    "taxProcedures",
                                    "loadNext",
                                  );
                              }}
                              page={pageNo}
                            />
                          )}
                        />
                      </Grid>
                    ) : null}
                  </Grid>
                  <Grid container spacing={2} className="my-[0px] ml-[3px]">
                    <Grid item xs={12}>
                      <Typography className="flex font-Saira font-[800] text-[19px] text-[#000000]">
                        {t("price")}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Typography className="flex font-Saira font-[800] text-[19px] text-[#000000]">
                        {t(defaultPriceList || "")}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        type="number"
                        {...register("price", {
                          required: t("This field is required."),
                        })}
                        onChange={(e: any) => {
                          const raw = e.target.value;
                          const parsed = parseFloat(raw);
                          const normalized =
                            raw !== "" &&
                            raw !== "." &&
                            !Number.isNaN(parsed) &&
                            parsed < 0
                              ? "0"
                              : raw;
                          setValue("price", normalized, {
                            shouldValidate: true,
                            shouldDirty: true,
                          });
                          const amount =
                            normalized === "" || normalized === "."
                              ? 0
                              : parseFloat(normalized) || 0;
                          setValue(
                            "priceWithTax",
                            (
                              amount +
                              (amount *
                                parseFloat(
                                  watch("taxProcedureId")?.percentageValue || 0,
                                )) /
                                100
                            )?.toFixed(2),
                          );
                          setValue(
                            "taxValue",
                            (
                              amount *
                              (watch("taxProcedureId")?.percentageValue / 100)
                            )?.toFixed(2),
                          );
                          if (!IsBtnEnabled) setIsBtnEnabled(true);
                        }}
                        label={
                          t("price") +
                          (loggedInUserCurrency
                            ? " (" + currencyLabel + ")"
                            : "") +
                          " *"
                        }
                        placeholder={t("phPackagePrice")}
                        title={
                          t("price") +
                          (loggedInUserCurrency
                            ? " (" + currencyLabel + ")"
                            : "")
                        }
                        fullWidth
                        error={!!errors.price}
                        helperText={errors.price?.message as string}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    {watch("isTaxable") && watch("taxProcedureId")?.value ? (
                      <>
                        <Grid item xs={12} md={3}>
                          <TextField
                            type="number"
                            {...register("tax")}
                            label={t("tax") + " (%)"}
                            placeholder={t("tax") + " (%)"}
                            title={t("tax") + " (%)"}
                            fullWidth
                            error={!!errors.tax}
                            helperText={errors.tax?.message as string}
                            InputLabelProps={{ shrink: true }}
                            InputProps={{
                              readOnly: true,
                              endAdornment: (
                                <InputAdornment position="end">
                                  <span className="font-Saira text-[13px]">
                                    {(watch("taxValue") || 0) +
                                      (loggedInUserCurrency
                                        ? " " + currencyLabel
                                        : "")}
                                  </span>
                                </InputAdornment>
                              ),
                            }}
                            value={
                              watch("taxProcedureId")?.percentageValue || 0
                            }
                          />
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <TextField
                            type="number"
                            {...register("priceWithTax")}
                            label={
                              t("priceWithTax") +
                              (loggedInUserCurrency
                                ? " (" + currencyLabel + ")"
                                : "")
                            }
                            placeholder={t("phPriceWithTaxZero")}
                            title={
                              t("priceWithTax") +
                              (loggedInUserCurrency
                                ? " (" + currencyLabel + ")"
                                : "")
                            }
                            fullWidth
                            error={!!errors.priceWithTax}
                            helperText={errors.priceWithTax?.message as string}
                            InputLabelProps={{ shrink: true }}
                            onChange={(e: any) => {
                              const value = parseFloat(e.target.value);
                              e.target.value = value < 0 ? 0 : value || 0;
                              const price =
                                parseFloat(e.target.value) /
                                (1 +
                                  (watch("taxProcedureId")?.percentageValue ||
                                    0) /
                                    100);
                              setValue(
                                "price",
                                price - Math.floor(price) > 0.9
                                  ? Math.round(price)
                                  : price?.toFixed(2),
                              );
                              setValue(
                                "taxValue",
                                (
                                  watch("price") *
                                  (watch("taxProcedureId")?.percentageValue /
                                    100)
                                )?.toFixed(2),
                              );
                              if (!IsBtnEnabled) setIsBtnEnabled(true);
                            }}
                          />
                        </Grid>
                      </>
                    ) : null}
                  </Grid>
                  <Grid container spacing={2} className="my-[0px] ml-[3px]">
                    <Grid item xs={12} md={3}>
                      <Controller
                        name="pricingType"
                        control={control}
                        defaultValue={null}
                        rules={{ required: t("This field is required.") }}
                        render={({ field }) => (
                          <DynamicSelect
                            label={t("pricingType") + " *"}
                            {...field}
                            options={pricingTypes}
                            isSearchable
                            onChange={(selectedOption: any) =>
                              field.onChange(selectedOption)
                            }
                            error={errors.pricingType?.message as string}
                            description={
                              <Box>
                                <Box sx={{ marginBottom: "6px" }}>
                                  {t("packagePricingTypeInfoIntro")}
                                </Box>
                                <Box component="ul" sx={{ margin: 0 }}>
                                  <li>
                                    <strong>{t("fixed")}:</strong>{" "}
                                    {t("packagePricingTypeInfoFixed")}
                                  </li>
                                  <li>
                                    <strong>{t("serviceSum")}:</strong>{" "}
                                    {t("packagePricingTypeInfoServiceSum")}
                                  </li>
                                </Box>
                              </Box>
                            }
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        type="number"
                        {...register("itemLimit", {
                          required: false,
                          validate: (value) =>
                            value === null ||
                            value === undefined ||
                            value === "" ||
                            Number(value) > 0
                              ? true
                              : t("amountGreaterThanZero"),
                        })}
                        label={
                          <Box className="flex items-center gap-1">
                            {t("itemLimit")}
                            <Tooltip
                              title={t("itemLimitPlaceholder", {
                                item: t("package"),
                              })}
                              enterDelay={0}
                              placement="top"
                              arrow
                              componentsProps={{
                                tooltip: {
                                  sx: {
                                    backgroundColor: "#fff",
                                    boxShadow: "0px 2px 8px rgba(0,0,0,0.12)",
                                    fontFamily: "Saira, sans-serif",
                                    color: "#111827",
                                    fontSize: '11px',
                                    fontWeight: "400",
                                    lineHeight: 1.5,
                                    padding: "10px",
                                  },
                                },
                              }}
                            >
                              <Info
                                sx={{
                                  fontSize: 17,
                                  color: "#6b7280",
                                  cursor: "pointer",
                                }}
                              />
                            </Tooltip>
                          </Box>
                        }
                        placeholder={t("phPackagePartsLimit")}
                        title={t("itemLimit")}
                        fullWidth
                        error={!!errors.itemLimit}
                        helperText={errors.itemLimit?.message as string}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                  </Grid>
                  <Grid container spacing={2} className="my-[0px] ml-[3px]">
                    <Grid item xs={12}>
                      <Typography className="flex font-Saira font-[800] text-[19px] text-[#000000]">
                        {t("replacementTime")}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        type="number"
                        {...register("packageEveryMiles")}
                        onChange={(e: any) => {
                          const value = e.target.value;
                          e.target.value = value < 0 ? 0 : value || "";
                          if (!IsBtnEnabled) setIsBtnEnabled(true);
                        }}
                        label={t("Mileage (km)")}
                        placeholder={t("phPackageMileage")}
                        title={t("Mileage (km)")}
                        fullWidth
                        error={!!errors.packageEveryMiles}
                        helperText={errors.packageEveryMiles?.message as string}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        type="number"
                        {...register("packageEveryPeriodTime")}
                        onChange={(e: any) => {
                          const value = e.target.value;
                          e.target.value =
                            value < 0 ? 0 : parseInt(value) || "";
                          if (!IsBtnEnabled) setIsBtnEnabled(true);
                        }}
                        label={t("Number of Days")}
                        placeholder={t("phPackageDays")}
                        title={t("Number of Days")}
                        fullWidth
                        error={!!errors.packageEveryPeriodTime}
                        helperText={
                          errors.packageEveryPeriodTime?.message as string
                        }
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                  </Grid>
                  {/* <Grid container spacing={2} className='my-[0px] ml-[3px]'>
                            <Grid item xs={12}>
                                <Typography className='flex font-Saira font-[800] text-[19px] text-[#000000]'>{t('extraNotes')}</Typography>
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <TextField 
                                    type="number"
                                    {...register("sortOrder")} 
                                    onChange={(e: any) => {
                                        const value = parseInt(e.target.value);
                                        e.target.value = value < 1 ? 1 : value > 100 ? 100 : value || "";
                                        if(!IsBtnEnabled) setIsBtnEnabled(true);
                                    }}
                                    label={t("Sort Order (Value 1-100)")}
                                    placeholder={t("Sort Order (Value 1-100)")}
                                    title={t("Sort Order (Value 1-100)")}
                                    fullWidth
                                    error={!!errors.sortOrder} 
                                    helperText={errors.sortOrder?.message as string} 
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <TextField
                                    {...register("description")}
                                    label={t('description')}
                                    placeholder={t('description')}
                                    title={t('description')}
                                    fullWidth
                                    error={!!errors.description}
                                    helperText={errors.description?.message as string}
                                    InputLabelProps={{ shrink: true }}
                                    multiline
                                    rows={1}
                                    sx={{
                                        '& .MuiInputBase-fullWidth.MuiInputBase-multiline': { width: '100%', paddingRight: '0px', paddingBottom: 0 },
                                        '& .MuiInputBase-inputMultiline': { resize: 'vertical', minHeight: '78px' }
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <TextField
                                    {...register("extraNotes")}
                                    label={t('extraNotes')}
                                    placeholder={t('extraNotes')}
                                    title={t('extraNotes')}
                                    fullWidth
                                    error={!!errors.extraNotes}
                                    helperText={errors.extraNotes?.message as string}
                                    InputLabelProps={{ shrink: true }}
                                    multiline
                                    rows={1}
                                    sx={{
                                        '& .MuiInputBase-fullWidth.MuiInputBase-multiline': { width: '100%', paddingRight: '0px', paddingBottom: 0 },
                                        '& .MuiInputBase-inputMultiline': { resize: 'vertical', minHeight: '78px' }
                                    }}
                                />
                            </Grid>
                        </Grid> */}
                </Grid>
              </>
            }
          />
        )}
      </div>
    </>
  );
}
